import { useState } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Input,
  Badge,
  Alert,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useBrand } from '../../brand/BrandProvider';
import {
  useUsageSummary,
  useUsageTop,
  useUsagePluginStatus,
} from './useNexusUsage';

const RESOURCE_TYPES = ['cpu_seconds', 'ram_bytes', 'bandwidth_bytes', 'storage_bytes'];

/**
 * Nexus Usage-Explorer (Spec 8 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-usage-explorer.md
 *
 * Drei Sub-Views als Tabs:
 *  1. Subscription-Summary — Bearer-Auth gegen bestehende /v1/usage/summary
 *  2. Top-N je Tenant — M2M gegen neue /v1/admin/usage/top
 *  3. Plugin-Health — M2M gegen neue /v1/admin/usage/plugin-status
 */
export function NexusUsagePage() {
  const [activeTab, setActiveTab] = useState('summary');

  return (
    <div>
      <PageHeader
        title="Nexus · Usage Explorer"
        subtitle="Triage von Verbrauchsdaten · Subscription-Summary · Top-N · Plugin-Health"
      />

      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            href="#"
            active={activeTab === 'summary'}
            onClick={(e) => { e.preventDefault(); setActiveTab('summary'); }}
          >
            Subscription-Summary
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            href="#"
            active={activeTab === 'top'}
            onClick={(e) => { e.preventDefault(); setActiveTab('top'); }}
          >
            Top-N je Tenant
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            href="#"
            active={activeTab === 'health'}
            onClick={(e) => { e.preventDefault(); setActiveTab('health'); }}
          >
            Plugin-Health
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="summary"><SummaryPanel /></TabPane>
        <TabPane tabId="top"><TopPanel /></TabPane>
        <TabPane tabId="health"><HealthPanel /></TabPane>
      </TabContent>
    </div>
  );
}

function SummaryPanel() {
  const [subId, setSubId] = useState('');
  const [start, setStart] = useState(() => fmtYmd(new Date(Date.now() - 30 * 86_400_000)));
  const [end, setEnd] = useState(() => fmtYmd(new Date()));

  const query = useUsageSummary({ subscriptionId: subId, start, end });

  return (
    <Card>
      <CardBody>
        <CardTitle tag="h6" className="text-uppercase small text-muted">Subscription-Summary</CardTitle>
        <p className="small text-muted">
          Aggregat (SUM für Akkumulatoren, AVG für Belegung) — entspricht
          Core&apos;s Abrechnungs-Call gegen <code>/v1/usage/summary</code>.
        </p>

        <Row className="g-2 mb-3 align-items-end">
          <Col md="4">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Subscription-ID</Label>
              <Input type="text" bsSize="sm" value={subId} onChange={(e) => setSubId(e.target.value)} placeholder="z.B. 42" />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Start</Label>
              <Input type="date" bsSize="sm" value={start} onChange={(e) => setStart(e.target.value)} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Ende</Label>
              <Input type="date" bsSize="sm" value={end} onChange={(e) => setEnd(e.target.value)} />
            </FormGroup>
          </Col>
        </Row>

        {!subId && (
          <Alert color="info" className="small mb-0">
            Gib eine Subscription-ID ein um das Aggregat zu laden.
          </Alert>
        )}

        {subId && (
          <SummaryTable query={query} />
        )}
      </CardBody>
    </Card>
  );
}

function SummaryTable({ query }) {
  if (query.isLoading) return <div className="text-muted small">Lade…</div>;
  if (query.isError) return <Alert color="warning" className="small mb-0">Fehler: {query.error?.message}</Alert>;
  const metrics = query.data?.metrics ?? [];
  if (metrics.length === 0) {
    return <Alert color="secondary" className="small mb-0">Keine Verbrauchsdaten im Zeitraum.</Alert>;
  }
  return (
    <table className="table table-sm">
      <thead>
        <tr>
          <th>Resource-Type</th>
          <th>Aggregation</th>
          <th className="text-end">Wert</th>
        </tr>
      </thead>
      <tbody>
        {metrics.map((m) => (
          <tr key={m.resource_type}>
            <td><code className="small">{m.resource_type}</code></td>
            <td><Badge color="secondary" pill className="orbit-code-badge-sm">{m.aggregation}</Badge></td>
            <td className="text-end font-monospace">{formatMetric(m.value, m.unit)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TopPanel() {
  const brand = useBrand();
  const [tenantId, setTenantId] = useState(() => brand.currentBrand?.id ? String(brand.currentBrand.id) : '');
  const [resourceType, setResourceType] = useState('cpu_seconds');

  const query = useUsageTop({ tenantId, resourceType, limit: 10 });

  return (
    <Card>
      <CardBody>
        <CardTitle tag="h6" className="text-uppercase small text-muted">Top-10 je Tenant</CardTitle>
        <p className="small text-muted">
          Subscriptions mit dem höchsten Verbrauch im aktuellen Zeitraum.
          Braucht D2-Hydration (subscription_id → tenant_id) in Nexus.
        </p>

        <Row className="g-2 mb-3 align-items-end">
          <Col md="4">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Tenant</Label>
              <Input type="select" bsSize="sm" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                <option value="">— Tenant wählen —</option>
                {(brand.brands ?? []).map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.displayName ?? b.slug} (#{b.id})
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Resource-Type</Label>
              <Input type="select" bsSize="sm" value={resourceType} onChange={(e) => setResourceType(e.target.value)}>
                {RESOURCE_TYPES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {tenantId ? <TopTable query={query} /> : (
          <Alert color="info" className="small mb-0">Wähle einen Tenant um die Top-N zu laden.</Alert>
        )}
      </CardBody>
    </Card>
  );
}

function TopTable({ query }) {
  if (query.isLoading) return <div className="text-muted small">Lade…</div>;
  if (query.isError) return <Alert color="warning" className="small mb-0">Fehler: {query.error?.message}</Alert>;
  const top = query.data?.top ?? [];
  if (top.length === 0) {
    return <Alert color="secondary" className="small mb-0">Keine Top-Daten — D2-Hydration noch nicht aktiv oder kein Sample im Zeitraum.</Alert>;
  }
  return (
    <table className="table table-sm align-middle">
      <thead>
        <tr>
          <th style={{ width: 50 }}>#</th>
          <th>Subscription</th>
          <th className="text-end">Verbrauch</th>
          <th className="text-end" style={{ width: 120 }}>Samples</th>
        </tr>
      </thead>
      <tbody>
        {top.map((row, i) => (
          <tr key={row.subscription_id}>
            <td>{i + 1}</td>
            <td>
              <code>{row.subscription_id}</code>
              {row.label && <div className="small text-muted">{row.label}</div>}
            </td>
            <td className="text-end font-monospace">{formatMetric(row.total, row.unit)}</td>
            <td className="text-end small text-muted">{row.samples ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HealthPanel() {
  const query = useUsagePluginStatus();
  const plugins = query.data?.plugins ?? [];
  const unhealthy = plugins.filter((p) => !p.healthy).length;

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <CardTitle tag="h6" className="text-uppercase small text-muted mb-0">Plugin-Health</CardTitle>
          <div>
            {unhealthy === 0 ? (
              <Badge color="success" pill>Alle Plugins healthy</Badge>
            ) : (
              <Badge color="warning" pill>{unhealthy} Plugin(s) stale</Badge>
            )}
          </div>
        </div>
        <p className="small text-muted">
          Letzter <code>collectUsage</code>-Sample je Plugin. Schwelle für
          <code> healthy</code> ist 12h. Auto-Refresh 60s.
        </p>

        <DataTable
          loading={query.isLoading}
          error={query.error}
          empty={!query.isLoading && plugins.length === 0}
          emptyMessage="Keine Plugin-Status-Daten."
          onRetry={() => query.refetch()}
        >
          <thead>
            <tr>
              <th>Plugin</th>
              <th style={{ width: 110 }}>Health</th>
              <th style={{ width: 150 }}>Last Sample</th>
              <th style={{ width: 110 }} className="text-end">Samples</th>
            </tr>
          </thead>
          <tbody>
            {plugins.map((p) => (
              <tr key={p.plugin_identifier}>
                <td className="fw-semibold">{p.plugin_identifier}</td>
                <td>
                  {p.healthy ? (
                    <Badge color="success" pill>healthy</Badge>
                  ) : (
                    <Badge color="warning" pill>stale &gt;12h</Badge>
                  )}
                </td>
                <td>{p.last_sample_at ? <RelativeTime iso={p.last_sample_at} /> : <span className="text-muted">nie</span>}</td>
                <td className="text-end small font-monospace">{p.sample_count ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </CardBody>
    </Card>
  );
}

function fmtYmd(d) {
  return d.toISOString().slice(0, 10);
}

function formatMetric(value, unit) {
  if (value == null) return '—';
  if (unit === 'B') {
    if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(2)} GB`;
    if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(2)} MB`;
    if (value >= 1024) return `${(value / 1024).toFixed(2)} KB`;
    return `${value} B`;
  }
  if (unit === 's') {
    if (value >= 3600) return `${(value / 3600).toFixed(1)} h`;
    if (value >= 60) return `${(value / 60).toFixed(1)} min`;
    return `${value} s`;
  }
  return `${value} ${unit ?? ''}`.trim();
}

